import { css } from '@emotion/react';

/**
 * Global styles including CSS reset, design tokens, and base styles.
 * Warm & Cozy theme with light/dark mode via [data-theme] attribute.
 */
export const globalStyles = css`
  /* ─── Light mode (default) CSS Variables ──────────────────────────────── */
  :root,
  [data-theme='light'] {
    /* Backgrounds */
    --color-bg-primary: #F8F9FA;
    --color-bg-secondary: #F0F2F5;
    --color-bg-tertiary: #E8EBF0;

    /* Brand */
    --color-primary-navy: #0F49BD;
    --color-primary-gold: #EEAD2B;
    --color-primary-terracotta: #C86B3E;

    /* Text */
    --color-text-primary: #1A2340;
    --color-text-secondary: #4A5568;
    --color-text-disabled: #9AA3B0;
    --color-text-inverse: #FFFFFF;

    /* Sidebar */
    --color-sidebar-bg: #0F49BD;
    --color-sidebar-text: #FFFFFF;
    --color-sidebar-text-muted: rgba(255, 255, 255, 0.65);
    --color-sidebar-active: #EEAD2B;
    --color-sidebar-hover: rgba(255, 255, 255, 0.1);

    /* Neutrals */
    --color-neutral-50: #f9f9fb;
    --color-neutral-100: #f0f0f3;
    --color-neutral-200: #e0e1e6;
    --color-neutral-300: #d9d9e0;
    --color-neutral-400: #cdced6;
    --color-neutral-500: #8b8d98;
    --color-neutral-600: #80838d;
    --color-neutral-700: #60646c;
    --color-neutral-800: #1c2024;
    --color-neutral-900: #1c2024;
    --color-white: #ffffff;
    --color-black: #000000;

    /* Semantic */
    --color-success: #12a594;
    --color-warning: #EEAD2B;
    --color-error: #e5484d;
    --color-info: #7ce2fe;

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    /* Card/Surface borders */
    --color-border: rgba(15, 73, 189, 0.12);
    --color-border-subtle: rgba(15, 73, 189, 0.06);
  }

  /* ─── Dark mode CSS Variables ──────────────────────────────────────────── */
  [data-theme='dark'] {
    /* Backgrounds */
    --color-bg-primary: #0D1B2E;
    --color-bg-secondary: #132238;
    --color-bg-tertiary: #1A2E48;

    /* Brand */
    --color-primary-navy: #5B9CF6;
    --color-primary-gold: #F4C025;
    --color-primary-terracotta: #C86B3E;

    /* Text */
    --color-text-primary: #E8F0FE;
    --color-text-secondary: #9DB8D9;
    --color-text-disabled: #5A7A9A;
    --color-text-inverse: #0D1B2E;

    /* Sidebar */
    --color-sidebar-bg: #0A1628;
    --color-sidebar-text: #E8F0FE;
    --color-sidebar-text-muted: rgba(232, 240, 254, 0.55);
    --color-sidebar-active: #F4C025;
    --color-sidebar-hover: rgba(255, 255, 255, 0.08);

    /* Neutrals */
    --color-neutral-50: #1A2E48;
    --color-neutral-100: #1F3550;
    --color-neutral-200: #284460;
    --color-neutral-300: #375572;
    --color-neutral-400: #4A6A88;
    --color-neutral-500: #5E819E;
    --color-neutral-600: #7A9AB8;
    --color-neutral-700: #9DB8D9;
    --color-neutral-800: #C5D8EE;
    --color-neutral-900: #E8F0FE;

    /* Semantic */
    --color-success: #12a594;
    --color-warning: #F4C025;
    --color-error: #e5484d;
    --color-info: #7ce2fe;

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.3);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.3);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3);

    /* Card/Surface borders */
    --color-border: rgba(91, 156, 246, 0.2);
    --color-border-subtle: rgba(91, 156, 246, 0.1);
  }

  /* ─── Static tokens (unchanged between modes) ──────────────────────────── */
  :root {
    /* Spacing */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-24: 6rem;
    --space-32: 8rem;

    /* Border Radius */
    --radius-sm: 0.25rem;
    --radius-md: 0.375rem;
    --radius-lg: 0.5rem;
    --radius-xl: 0.75rem;
    --radius-2xl: 1rem;
    --radius-full: 9999px;

    /* Focus */
    --focus-ring: 0 0 0 3px rgba(238, 173, 43, 0.4);
    --focus-ring-offset: 2px;

    /* Transitions */
    --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
    --transition-slow: 400ms cubic-bezier(0.4, 0, 0.2, 1);

    /* Typography */
    --font-family-base: var(--font-inter, Inter), -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    --font-family-heading: var(--font-noto-serif, 'Noto Serif'), Georgia, 'Times New Roman', serif;
    --font-family-mono: 'SF Mono', ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'Courier New', monospace;

    /* Sidebar layout dimensions */
    --sidebar-width: 240px;
    --sidebar-collapsed-width: 72px;
  }

  /* ─── CSS Reset ────────────────────────────────────────────────────────── */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    -webkit-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scroll-behavior: smooth;
  }

  @media (prefers-reduced-motion: reduce) {
    html {
      scroll-behavior: auto;
    }

    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  body {
    font-family: var(--font-family-base);
    font-size: 1rem;
    line-height: 1.5;
    color: var(--color-text-primary);
    background: var(--color-bg-primary);
    min-height: 100vh;
    transition: background var(--transition-normal), color var(--transition-normal);
  }

  /* ─── Typography ───────────────────────────────────────────────────────── */
  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-family-heading);
    font-weight: 700;
    line-height: 1.2;
    color: var(--color-text-primary);
  }

  h1 { font-size: 2.25rem; }
  h2 { font-size: 1.875rem; }
  h3 { font-size: 1.5rem; }
  h4 { font-size: 1.25rem; }
  h5 { font-size: 1.125rem; }
  h6 { font-size: 1rem; }

  @media (max-width: 639px) {
    h1 { font-size: 1.875rem; }
    h2 { font-size: 1.5rem; }
    h3 { font-size: 1.25rem; }
  }

  p {
    margin-bottom: 1em;
  }

  p:last-child {
    margin-bottom: 0;
  }

  /* ─── Links ────────────────────────────────────────────────────────────── */
  a {
    color: var(--color-primary-navy);
    text-decoration: none;
    transition: color var(--transition-fast);
  }

  a:hover {
    color: var(--color-primary-gold);
  }

  a:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
    border-radius: var(--radius-sm);
  }

  /* ─── Form Elements ────────────────────────────────────────────────────── */
  button, input, select, textarea {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  button {
    cursor: pointer;
    background: none;
    border: none;
  }

  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  /* ─── Images ───────────────────────────────────────────────────────────── */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  ul, ol {
    list-style: none;
  }

  /* ─── Accessibility ────────────────────────────────────────────────────── */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    background: var(--color-primary-navy);
    color: var(--color-text-inverse);
    padding: var(--space-3) var(--space-6);
    text-decoration: none;
    border-radius: 0 0 var(--radius-md) 0;
    z-index: 1000;
  }

  .skip-link:focus {
    top: 0;
  }

  /* ─── Print ────────────────────────────────────────────────────────────── */
  .print-only {
    display: none;
  }

  @media print {
    .no-print {
      display: none !important;
    }

    .print-only {
      display: block !important;
    }
  }
`;
